import clsx from "clsx";
import type React from "react";
import type { PropsWithChildren } from "react";
import styles from "./Text.module.scss";

type Variant = "large" | "medium" | "small" | "xsmall";
type Color =
  | "primary"
  | "secondary"
  | "tertiary"
  | "inverse"
  | "link"
  | "success"
  | "warning"
  | "error";
type Weight = "light" | "normal" | "medium" | "semibold" | "bold";
type Align = "left" | "center" | "right";

export type TextAsProp =
  | "p"
  | "label"
  | "span"
  | "div"
  | "blockquote"
  | "pre"
  | "code"
  | "em"
  | "strong"
  | "small"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6";

interface BaseTextProps
  extends PropsWithChildren<React.HTMLAttributes<HTMLElement>> {
  as?: TextAsProp;
  variant: Variant;
  color?: Color;
  weight?: Weight;
  align?: Align;
  truncate?: boolean;
  uppercase?: boolean;
  capitalize?: boolean;
  className?: string;
}

interface LabelProps extends BaseTextProps {
  as: "label";
  htmlFor: string;
}

interface NonLabelProps extends BaseTextProps {
  as?: Exclude<TextAsProp, "label">;
}

export type TextProps = LabelProps | NonLabelProps;

export const Text: React.FC<TextProps> = ({
  as = "p",
  variant,
  color,
  weight,
  align,
  truncate = false,
  uppercase = false,
  capitalize = false,
  className,
  children,
  ...props
}) => {
  const Component = as;
  const combinedClassName = clsx(
    styles[variant],
    color && styles[color],
    weight && styles[weight],
    align && styles[align],
    {
      [styles.truncate]: truncate,
      [styles.uppercase]: uppercase,
      [styles.capitalize]: capitalize,
    },
    className
  );

  return (
    <Component {...props} className={combinedClassName}>
      {children}
    </Component>
  );
};
